import React from 'react';
import LegalPageLayout, { LegalSection } from '../components/LegalPageLayout';

const RefundPolicyPage = () => {
  return (
    <LegalPageLayout
      testId="refund-policy-page"
      eyebrow="Legal"
      title="Return & Refund Policy"
      lastUpdated="February 2026"
    >
      <LegalSection number="1" title="Scope">
        <p>
          This policy applies to payments made directly to Sebastian Alliance
          Group LLC ("SAG") through the SAG Payment Center, corporate
          invoices, subscription products and professional services fees.
        </p>
        <p>
          For payments made to a merchant, portfolio company or third-party
          service that uses SAG technology to accept payment (for example
          Caribbean Payments Alliance merchants, hotels using CloudSync Pro,
          or clients using APC Estate Flow), refunds are handled by that
          merchant under their own return and refund policy. SAG supports
          the merchant in processing an approved refund through the
          underlying processor.
        </p>
      </LegalSection>

      <LegalSection number="2" title="Refund window">
        <p>
          Unless a different period is stated on the specific product page,
          invoice or written agreement:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>One-off services and setup fees</strong> — you may
            request a full refund within <strong>seven (7) calendar days</strong>{' '}
            of payment, provided work has not yet started.
          </li>
          <li>
            <strong>Subscriptions and recurring plans</strong> — you may
            cancel at any time. The current billing period is non-refundable
            after it has begun; you will not be charged for future periods
            once cancelled.
          </li>
          <li>
            <strong>Custom development, migrations and consulting</strong> —
            refundable pro-rata for work not yet delivered, less any
            non-recoverable third-party costs already incurred (for example
            domain registrations, licensed software, or bank onboarding
            fees).
          </li>
          <li>
            <strong>Digital goods and licences already delivered</strong>{' '}
            (for example a delivered software plugin key, a purchased
            template, or a paid report) — non-refundable once the licence
            has been issued to you, except where required by law.
          </li>
        </ul>
      </LegalSection>

      <LegalSection number="3" title="How to request a refund">
        <p>To request a refund, email{' '}
          <a
            href="mailto:ecommerce@sebasalliance.net"
            className="text-[#C9A227] hover:underline"
          >
            ecommerce@sebasalliance.net
          </a>{' '}
          from the address used at checkout and include:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>The invoice number or Payment Center reference / session ID.</li>
          <li>The date and amount of the payment.</li>
          <li>The last four digits of the card used (if paid by card).</li>
          <li>A brief description of the reason for the refund.</li>
        </ul>
        <p>
          We aim to acknowledge every request within one (1) business day.
          Approved refunds are typically issued within five (5) business
          days to the original payment method. Depending on your card issuer
          or bank, the credit may take a further 3–10 business days to
          appear on your statement.
        </p>
      </LegalSection>

      <LegalSection number="4" title="Cancellations">
        <p>
          You may cancel any active subscription from your merchant or
          admin dashboard or by emailing our support address above. If you
          cancel before the next renewal date, no further charges will be
          made. Amounts already billed for the current period are not
          refunded except where required by law.
        </p>
      </LegalSection>

      <LegalSection number="5" title="Duplicate charges and errors">
        <p>
          If you believe you have been charged twice for the same invoice,
          or in an incorrect amount, please contact us within{' '}
          <strong>sixty (60) days</strong> of the charge. We will
          investigate and, if confirmed, refund the duplicate or excess
          amount to the original payment method.
        </p>
      </LegalSection>

      <LegalSection number="6" title="Chargebacks and disputes">
        <p>
          If you do not recognise a charge or disagree with an amount,
          please contact us first — most issues are resolved faster
          directly than through a bank chargeback. Where a chargeback is
          filed, SAG cooperates fully with the acquiring bank and card
          networks, and may submit transaction, device, KYC and
          verification evidence to defend legitimate charges. Fraudulent or
          bad-faith chargebacks may result in the account being suspended,
          collection of the disputed amount, and legal action where
          appropriate.
        </p>
      </LegalSection>

      <LegalSection number="7" title="Non-refundable items">
        <p>The following are not refundable:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Third-party fees already paid on your behalf (for example bank underwriting, government filing fees, domain registrations, courier costs).</li>
          <li>Delivered digital goods, issued licence keys and completed custom development milestones that were accepted in writing.</li>
          <li>Investor Portal access fees or investor due-diligence packs after materials have been unlocked or downloaded.</li>
          <li>Time-based services (consulting, engineering) already performed.</li>
        </ul>
      </LegalSection>

      <LegalSection number="8" title="Changes to this policy">
        <p>
          We may update this policy from time to time; the version in effect
          at the time of your payment applies to that payment. Material
          changes will be posted on this page with an updated "Last updated"
          date.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
};

export default RefundPolicyPage;
